import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import DOMPurify from "dompurify";
import { styleMessageText } from "../helperFunctions/emailPreviewHtml";

function escapeHtml(text) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Only these survive a paste - matches exactly what this editor's own formatting model
// (the TOOLBAR_BUTTONS shorthand, plus links) can express. Anything else pasted in -
// colours, fonts, layout divs/spans, classes - is incidental styling picked up from wherever
// it was copied, not something a template is meant to carry, and it would otherwise get
// baked verbatim into the stored template and the real outgoing email.
const PASTE_ALLOWED_TAGS = ["b", "strong", "i", "em", "u", "br", "a"];
const PASTE_ALLOWED_ATTR = ["href"];

// Real Gmail messages (as opposed to a fresh compose draft) lay paragraphs out as sibling
// <div>s rather than <br>-separated lines. Stripped down to PASTE_ALLOWED_TAGS, those divs
// vanish along with their line breaks and every paragraph runs together - so block
// boundaries are converted to <br> BEFORE sanitizing, while they're still there to find.
function normalizeBlockBreaks(html) {
  return html
    .replace(/<\/(div|p|li|tr)>/gi, "$&<br>")
    .replace(/(<br\s*\/?>\s*){3,}/gi, "<br><br>")
    .replace(/(<br\s*\/?>\s*)+$/i, "");
}

// Shared with TemplateCard, which renders the actual toolbar buttons (positioned in the
// card footer) and calls wrapSelection(marker) on this component via the forwarded ref.
export const TOOLBAR_BUTTONS = [
  { label: "B", marker: "**", title: "Bold", style: { fontWeight: 700 } },
  { label: "I", marker: "*", title: "Italic", style: { fontStyle: "italic" } },
  { label: "U", marker: "__", title: "Underline", style: { textDecoration: "underline" } },
  { label: "S", marker: "^^", title: "Small text", style: { fontSize: "0.75em" } },
];

// A contentEditable box that accepts pasted rich text (e.g. copied from a Gmail compose/
// view pane) and keeps its formatting - bold, italic, colour, inline styles - instead of
// flattening it to plain text the way a <textarea> would. Pasted HTML is sanitised with
// DOMPurify (default allowlist: strips <script>, event handler attributes, javascript:
// hrefs, etc. - keeps formatting tags and inline `style`) before insertion, since whatever
// ends up in here later gets rendered via dangerouslySetInnerHTML in the "Show as it
// appears" preview and, ultimately, becomes the real email HTML.
//
// Deliberately uncontrolled: `initialHtml` only seeds content on mount (component is
// remounted via `key` by the caller whenever it needs a fresh seed - e.g. switching which
// template is being edited). Re-applying `initialHtml` on every render/keystroke would
// reset the cursor position to the start of the field.
const RichTemplateEditor = forwardRef(function RichTemplateEditor({ id, initialHtml, onChange }, ref) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = initialHtml;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function reportChange() {
    onChange(editorRef.current.innerHTML);
  }

  function handlePaste(e) {
    e.preventDefault();

    let html = e.clipboardData.getData("text/html");
    let text = e.clipboardData.getData("text/plain");
    let clean = html
      ? DOMPurify.sanitize(normalizeBlockBreaks(html), {
          ALLOWED_TAGS: PASTE_ALLOWED_TAGS,
          ALLOWED_ATTR: PASTE_ALLOWED_ATTR,
        })
      : escapeHtml(text).replace(/\n/g, "<br>");

    document.execCommand("insertHTML", false, clean);
    reportChange();
  }

  // Converts any **bold**/__underline__/etc. shorthand just typed into real formatting as
  // soon as the field loses focus - so freshly-typed shorthand ends up looking the same as
  // pasted-in formatting, without touching the cursor while the user is actively typing
  // (which live per-keystroke conversion would require, and is the fragile part of
  // contentEditable rich-text editors to get right).
  function handleBlur() {
    let converted = styleMessageText(editorRef.current.innerHTML);
    if (converted !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = converted;
      reportChange();
    }
  }

  // Wraps the current selection in a pair of shorthand markers (e.g. "**" for bold) -
  // literally just inserting the marker characters around the selected text, the same as
  // typing them by hand. With nothing selected, inserts an empty pair and places the cursor
  // between them so typing continues inside the markers. Combining two markers on the same
  // selection (e.g. Bold then Italic) naturally nests into ***text*** - no separate button
  // needed for that combination. Exposed via ref so the toolbar buttons (rendered by
  // TemplateCard, positioned in the card footer rather than right above the editor) can
  // call it without needing direct DOM access to this editor.
  function wrapSelection(marker) {
    let editor = editorRef.current;
    let selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    let range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return;

    // extractContents() keeps the selection's actual DOM (e.g. an <a> link) intact instead
    // of collapsing it to range.toString()'s plain text, which would silently drop any
    // markup - just the href of a wrapped link, most often.
    let hadSelection = !range.collapsed;
    let content = range.extractContents();
    let startMarker = document.createTextNode(marker);
    let endMarker = document.createTextNode(marker);

    let fragment = document.createDocumentFragment();
    fragment.appendChild(startMarker);
    fragment.appendChild(content);
    fragment.appendChild(endMarker);
    range.insertNode(fragment);

    let newRange = document.createRange();
    if (hadSelection) {
      newRange.setStartAfter(endMarker);
    } else {
      newRange.setStartAfter(startMarker);
    }
    newRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(newRange);

    reportChange();
  }

  // Strips all formatting from the current selection back to plain text - the way out for
  // markup that shouldn't be there (leftovers from a messy paste, or just a change of mind),
  // regardless of what tags/attributes it's actually made of. <br>s and block boundaries
  // inside the selection are converted to real newlines first so a multi-line selection
  // doesn't collapse onto one line once every tag is gone.
  function clearFormatting() {
    let editor = editorRef.current;
    let selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    let range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer) || range.collapsed) return;

    let scratch = document.createElement("div");
    scratch.appendChild(range.cloneContents());
    scratch.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
    scratch.querySelectorAll("div, p").forEach((block) => block.after("\n"));
    let plainText = scratch.textContent.replace(/\n{3,}/g, "\n\n");

    range.deleteContents();

    let fragment = document.createDocumentFragment();
    let lines = plainText.split("\n");
    lines.forEach((line, i) => {
      fragment.appendChild(document.createTextNode(line));
      if (i < lines.length - 1) fragment.appendChild(document.createElement("br"));
    });
    let lastNode = fragment.lastChild;
    range.insertNode(fragment);

    if (lastNode) {
      let newRange = document.createRange();
      newRange.setStartAfter(lastNode);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }

    reportChange();
  }

  useImperativeHandle(ref, () => ({ wrapSelection, clearFormatting }));

  return (
    <div
      ref={editorRef}
      id={id}
      className="templateMessageEditor richTemplateEditor"
      contentEditable
      suppressContentEditableWarning
      onInput={reportChange}
      onPaste={handlePaste}
      onBlur={handleBlur}
    />
  );
});

export default RichTemplateEditor;
